// ----------------------------------------------------------------------
// <copyright file="Izenda.ValidationManager.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------
!(function() {
  require('share/utils/Izenda.StringUtilities');
  var defaultStrategy = require('./Izenda.ValidateStrategy');

  const { filter, isArray, isEmpty, forEach, invoke, map, get, isNil, toLower, placeholder } = require('lodash/fp');

  function ValidationManager(validatorStrategy) {
    this.validatorStrategy = validatorStrategy || defaultStrategy;
    this.components = [];
    this.modelErrors = [];
  }

  ValidationManager.prototype.attachComponent = function(component) {
    if (this.components.indexOf(component) < 0) {
      this.components.push(component);
    }
  };

  ValidationManager.prototype.detachComponent = function(component) {
    var idx = this.components.indexOf(component);
    this.components.splice(idx, 1);
  };

  ValidationManager.prototype.validate = function(field, group) {
    var coms = filter(function(com) {
      return (
        (!field ||
          (!isArray(field) && com.props.field === field) ||
          (isArray(field) && field.indexOf(com.props.field) > -1)) &&
        (!group || com.props.group === group)
      );
    }, this.components);

    if (isEmpty(coms)) {
      Promise.resolve(true);
    }

    var strategy = this.validatorStrategy;
    var maps = {
      attributes: {},
      rules: {}
    };
    var computeKey = this.getKey;
    var vals = placeholder(coms)
      .chain()
      .map(function(com) {
        return {
          key: computeKey(com),
          value: com.getValue(),
          rules: strategy.parseRules(com.props.validations)
        };
      })
      .reduce(function(rulesMap, keyedRules) {
        rulesMap.rules[keyedRules.key] = keyedRules.rules;
        rulesMap.attributes[keyedRules.key] = keyedRules.value;

        return rulesMap;
      }, maps)
      .value();

    return strategy.validate(vals.attributes, vals.rules).then(
      function() {
        forEach(invoke('setSuccess'), coms);
        return true;
      },
      function(errors) {
        var valErrors = strategy.parseErrors(errors);
        if (field) {
          coms.map(function(com) {
            var key = computeKey(com);
            valErrors[key] && com.setError(valErrors[key]);
          });
        } else {
          placeholder(coms)
            .chain()
            .map(function(com) {
              var key = computeKey(com);
              return {
                msg: valErrors[key],
                com: com
              };
            })
            .forEach(function(item) {
              item.msg ? item.com.setError(item.msg) : item.com.setSuccess();
            })
            .commit();
        }

        return false;
      }
    );
  };

  ValidationManager.prototype.getFieldValue = function(field, group) {
    ensure(field, 'Field name must be defined');

    var com = placeholder(this.components)
      .chain()
      .filter(['props.group', group])
      .find(['props.field', field])
      .value();

    ensure(com, 'Cannot find component that holds value of field %s', field);

    return com.getValue();
  };

  /**
   * Update validators by errors from outside (normally it comes from API)
   * @param  {Array} errors Errors
   */
  ValidationManager.prototype.updateErrors = function(errors) {
    if (!errors) {
      return;
    }

    var computeKey = this.getKey;

    // Update model errors that don't have key value
    this.setModelErrors(
      filter(function(err) {
        return isNil(err.key) || isEmpty(err.key);
      }, errors)
    );

    // Update error by group and field
    placeholder(this.components)
      .chain()
      .map(function(com) {
        var comErrors = filter(function(err) {
          var errorKey = String.hashKeyId(err.key);
          return (
            (com.props.group && toLower(errorKey) === toLower(com.props.group)) ||
            toLower(errorKey) === toLower(computeKey(com))
          );
        }, errors);
        return {
          errors: comErrors,
          component: com
        };
      })
      .filter(function(errCom) {
        return !isEmpty(errCom.errors);
      })
      .forEach(function(errCom) {
        errCom.component.setError(errCom.errors);
      })
      .commit();
  };

  /**
   * Get computed key of component
   * @param  {Object} component React component
   * @return {String}           Key string
   */
  ValidationManager.prototype.getKey = function(component) {
    return String.hashKeyId((component.props.group ? component.props.group + '.' : '') + component.props.field);
  };

  ValidationManager.prototype.setModelErrors = function(errors) {
    this.modelErrors = errors;
  };

  /**
   * Reset field or group components to pristine state
   * @param {String} field Validation field
   * @param {String} group Validation group
   */
  ValidationManager.prototype.reset = function(field, group) {
    placeholder(this.components)
      .chain()
      .filter(function(com) {
        return !group || com.props.group === group;
      })
      .filter(function(com) {
        return !field || com.props.field === field;
      })
      .forEach(invoke('setPristine'))
      .commit();
  };

  /**
   * Notify field or group components that their value has been changed,
   * but unvalidated.
   * @param {String} field Validation field
   * @param {String} group Validation group
   */
  ValidationManager.prototype.unvalidatedChange = function(field, group) {
    placeholder(this.components)
      .chain()
      .filter(function(com) {
        return !group || com.props.group === group;
      })
      .filter(function(com) {
        return !field || com.props.field === field;
      })
      .forEach(invoke('setUnvalidatedChange'))
      .commit();
  };

  module.exports = ValidationManager;
})();



// WEBPACK FOOTER //
// ./ui/share/validation/Izenda.ValidationManager.js